import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export function useSubgroups(params?: { search?: string; type?: string }) {
  return useQuery({
    queryKey: ['subgroups', params],
    queryFn: async () => {
      const res = await api.get('/subgroups', { params });
      return res.data;
    },
  });
}

export function useSubgroup(id: string | number | undefined) {
  return useQuery({
    queryKey: ['subgroup', id],
    queryFn: async () => {
      const res = await api.get(`/subgroups/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useMyDriveObligations() {
  return useQuery({
    queryKey: ['my-drive-obligations'],
    queryFn: async () => {
      const res = await api.get('/subgroups/my-drive-obligations');
      return res.data;
    },
  });
}

export function useCreateSubgroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string; type?: string }) => {
      const res = await api.post('/subgroups', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subgroups'] });
      toast.success('Saved successfully');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useUpdateSubgroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number | string; name: string; description?: string; type?: string }) => {
      const res = await api.put(`/subgroups/${id}`, data);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subgroups'] });
      queryClient.invalidateQueries({ queryKey: ['subgroup', data?.data?.id] });
      toast.success('Subgroup updated successfully');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useExportSubgroups() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get('/subgroups/export-all', { responseType: 'blob' });
      return res.data;
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useExportSubgroupDetail() {
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.get(`/subgroups/${id}/export-detail`, { responseType: 'blob' });
      return res.data;
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useDownloadSubgroupTemplate() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get('/subgroups/template', { responseType: 'blob' });
      return res.data;
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useImportSubgroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/subgroups/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subgroups'] });
      toast.success(data?.message || 'Import complete');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useDeleteSubgroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.delete(`/subgroups/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subgroups'] });
      toast.success('Subgroup deleted successfully');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}
